import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { differenceInDays } from 'date-fns';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';

import { PremiumTrialOnboardingModal } from '@confluence/premium-onboarding';
import { TriggerFrom } from '@confluence/premium-onboarding/entry-points/PremiumTrialOnboardingModal';
import { useSessionData, AccessStatus } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition';
import { ErrorDisplay } from '@confluence/error-boundary';

import { TrialDetailsQuery } from '../TrialDetailsQuery.graphql';
import type { TrialDetailsQuery as TrialDetailsQueryType } from '../__types__/TrialDetailsQuery';

const getTrialDaysLeft = (trialEndDate: string | null | undefined) => {
	if (!trialEndDate) {
		return null;
	}

	// +1 day to round up for fractional days
	return differenceInDays(new Date(trialEndDate), new Date()) + 1;
};

const i18n = defineMessages({
	explorePremiumTitle: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.title',
		defaultMessage: 'Try Premium features',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumDescription: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.description',
		defaultMessage:
			'You’re on a 30-day free trial of Confluence Premium. See what’s included and take it out for a spin.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumCTA: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.cta',
		defaultMessage: 'See Premium features',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button open a modal that teaches them about premium features',
	},
});

export const HTV2ExplorePremiumCTA = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { accessStatus, isLoggedIn, edition } = useSessionData();
	const isLicensed = accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const skipTrialDetailsQuery = Boolean(
		!isLoggedIn || !isLicensed || edition !== ConfluenceEdition.PREMIUM,
	);

	const {
		data: trialDetailsData,
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useQuery<TrialDetailsQueryType>(TrialDetailsQuery, {
		skip: skipTrialDetailsQuery,
	});

	const formattedTrialsDaysLeft = getTrialDaysLeft(trialDetailsData?.license?.trialEndDate) ?? 0;

	const handleClick = () => {
		setIsModalOpen((isModalOpen) => !isModalOpen);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2ActionCta',
				attributes: {
					onboardingAction: 'explorePremium',
				},
			},
		}).fire();
	};

	return (
		<>
			<>{trialDetailsError && <ErrorDisplay error={trialDetailsError} />}</>
			{isModalOpen && (
				<PremiumTrialOnboardingModal
					onClose={() => setIsModalOpen(false)}
					triggerFrom={TriggerFrom.HOME_ONBOARDING_TAKEOVER_V2}
					daysLeft={formattedTrialsDaysLeft}
				/>
			)}
			<Button
				isDisabled={Boolean(
					formattedTrialsDaysLeft <= 0 || skipTrialDetailsQuery || trialDetailsError,
				)}
				isLoading={trialDetailsLoading}
				onClick={handleClick}
				appearance="default"
			>
				<FormattedMessage {...i18n.explorePremiumCTA} />
			</Button>
		</>
	);
};
